import React from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  CircularProgress,
  Chip,
} from "@mui/material";

// Import scenario hooks - hooks are called at the TOP LEVEL of this component,
// which is what makes this component the correct fix for the Rules of Hooks
// violation that existed in VaccinationTabs when hooks were called inside
// renderCurrentVaccinationDose(), a plain function, not a component.
import { usePWCurrentLogicEnhanced } from "./scenarios/pregnant-women-current";
import { useChildCurrentLogicEnhanced } from "./scenarios/children-current";

/**
 * VaccineCurrentDoseRow
 *
 * Renders a single dose row in the Current Vaccination tab (radio button variant).
 *
 * WHY THIS IS A SEPARATE COMPONENT:
 * The scenario hooks (usePWCurrentLogicEnhanced, useChildCurrentLogicEnhanced) must
 * be called at the top level of a React component, never inside a helper function or
 * a conditional block. The old code in VaccinationTabs called these hooks inside
 * renderCurrentVaccinationDose(), which is a plain function called from inside a
 * .map(). That is a Rules of Hooks violation. By extracting this into its own
 * component, each dose row mounts as an independent component instance. React then
 * tracks hook calls on a per-component basis, which is exactly what the rules require.
 *
 * HOOK CALL STRATEGY:
 * Both PW and CHILD hooks are always called unconditionally (lines below). The one
 * whose result is not needed for the current beneficiaryType is simply discarded.
 * This is the standard React pattern for conditional hook selection - you cannot
 * call hooks inside an if/else, so you call both and use only the relevant one.
 *
 * PROP CONTRACT:
 * @param {Object}   vaccine               - Vaccine object with id, name, availableDoses, material
 * @param {Object}   dose                  - Individual dose object from vaccine.availableDoses
 * @param {number}   doseIndex             - Zero-based index of this dose in availableDoses
 * @param {string}   beneficiaryType       - 'PW' | 'CHILD'
 * @param {Object}   profileData           - Full profile object: { lmc_date, vaccination } for PW
 *                                           or { dob } for CHILD. Note: must be the FULL object,
 *                                           not just the date string.
 * @param {Array}    administeredVaccinations - Vaccination history array from the profile API
 * @param {Function} decryptFunction        - Stable decrypt function reference
 */
const VaccineCurrentDoseRow = ({
  vaccine,
  dose,
  doseIndex,
  beneficiaryType,
  profileData,
  administeredVaccinations,
  decryptFunction,
  identityContext = null,
  forceDisabled = false,
  forceDisabledReason = "",
}) => {
  // ---- HOOK CALLS (must be unconditional and at top level) ----

  // PW hook: always called regardless of beneficiaryType.
  // profileData for PW must be the full object { lmc_date, vaccination } because
  // getPWCurrentDoseStatus destructures it as `const { lmc_date, vaccination } = profileData`.
  // Passing only profileData?.lmc_date here would break PW logic silently.
  const pwLogic = usePWCurrentLogicEnhanced(
    vaccine,
    profileData,    // full profileData object, NOT profileData?.lmc_date
    decryptFunction,
    identityContext
  );

  // Child hook: always called regardless of beneficiaryType.
  // useChildCurrentLogicEnhanced expects childDob and administeredDoses as separate args,
  // not as a combined profileData object.
  const childLogic = useChildCurrentLogicEnhanced(
    vaccine,
    profileData?.dob,            // DOB string for child age calculation
    administeredVaccinations,    // vaccination history array
    decryptFunction,
    identityContext
  );

  // ---- SELECT THE CORRECT LOGIC RESULT ----
  // Now that both hooks have been called (satisfying the rules), pick the one to use.
  const currentLogic = beneficiaryType === "PW" ? pwLogic : childLogic;

  // ---- LOADING STATE ----
  // isReady is false when required data has not arrived yet (e.g., profile API still loading).
  if (!currentLogic.isReady) {
    return (
      <Box sx={{ py: 0.5 }}>
        <CircularProgress size={12} sx={{ mr: 1 }} />
        <Typography component="span" fontSize={12} color="text.disabled">
          Loading dose {doseIndex + 1}...
        </Typography>
      </Box>
    );
  }

  // ---- DOSE CONFIG ----
  // getDoseConfig returns the full DoseConfig contract: { status, enabled, showRadio, date, reason }
  const doseConfig = currentLogic.getDoseConfig(doseIndex);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.5,
        // Only show the divider between doses, not after the last one
        borderBottom:
          doseIndex < (vaccine.availableDoses?.length ?? 1) - 1
            ? "1px solid #eee"
            : "none",
      }}
    >
      {/* Radio button - shown only when the dose is currently eligible */}
      {(doseConfig.status === "enabled" || doseConfig.status === "Eligible") && vaccine.availableDoses?.length > 1 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            value={dose.doseName}
            control={<Radio size="small" disabled={forceDisabled} />}
            label={dose.doseName}
            sx={{ margin: 0 }}
            disabled={forceDisabled}
          />
        </Box>
      ) : (
        // Non-eligible doses show as plain text with visual differentiation
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            fontSize={14}
            sx={{
              margin: 0,
              color:
                doseConfig.status === "administered" || ((doseConfig.status === "enabled" || doseConfig.status === "Eligible") && vaccine.availableDoses?.length === 1)
                  ? "text.primary"
                  : "text.disabled",
              pl: 2,
              fontWeight: doseConfig.status === "administered" || ((doseConfig.status === "enabled" || doseConfig.status === "Eligible") && vaccine.availableDoses?.length === 1) ? 500 : 400,
            }}
          >
            {dose.doseName}
          </Typography>
        </Box>
      )}

      {/* Right-side label: date for administered, reason/status for others */}
      <Typography
        fontSize={12}
        color={
          doseConfig.status === "administered"
            ? "text.primary"
            : doseConfig.status === "Eligible" || doseConfig.status === "enabled"
            ? (forceDisabled ? "warning.main" : "success.main")
            : "warning.main"
        }
        sx={{
          fontWeight: doseConfig.status === "administered" ? 500 : 400,
        }}
      >
        {doseConfig.status === "administered"
          ? doseConfig.date || "Updated"
          : forceDisabled ? forceDisabledReason : ((doseConfig.reason || doseConfig.status) === "Updated" ? "Administered" : (doseConfig.reason || doseConfig.status))}
      </Typography>
    </Box>
  );
};

// React.memo prevents this component from re-rendering when the parent re-renders
// but none of its props have changed. Because VaccineCurrentDoseRow is rendered
// inside a .map() over doses, without memo every dose row re-renders whenever the
// parent's state changes (e.g., when the user selects a different radio button),
// even if this specific dose's data is unaffected.
export default React.memo(VaccineCurrentDoseRow);
